/**
 * Query hooks for workflow suite benchmarks (suites, trends, comparisons, cleanup).
 *
 * @module hooks/useBenchmarkWorkflowData
 * @see docs/api/services/benchmark.md
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import type {
  BenchmarkRunStatus,
  BenchmarkTriggerType,
  BenchmarkWorkflowRunMetrics,
  BenchmarkWorkflowTrendPoint,
  EnvironmentSnapshot,
} from '@chatsdet/types';

/** One arm of an ad-hoc A/B benchmark (stored in BenchmarkRun.metadata.arms). */
export type BenchmarkArm = {
  label: string;
  knobOverrides?: Record<string, unknown>;
  flagOverrides?: Record<string, boolean | 'treatment' | 'control'>;
};

/** Per-run row of an ad-hoc A/B comparison (matches the backend ArmComparisonRow). */
export type ArmComparisonRow = {
  /** Arm label; '' for ordinary suite rows. */
  arm: string;
  workflowName: string;
  sourceWorkflowId: string;
  runNumber: number;
  isCached: boolean;
  status: BenchmarkRunStatus;
  durationMs: number | null;
  totalTokens: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  successfulNodes: number | null;
  failedNodes: number | null;
  totalNodes: number | null;
  cachedSteps: number | null;
  l2RecoverySteps: number | null;
  ejectedSteps: number | null;
  agentSteps: number | null;
};

export type BenchmarkRun = {
  id: string;
  /** Null for ad-hoc A/B runs (no saved config). */
  configId: string | null;
  jobId: string | null;
  status: BenchmarkRunStatus;
  triggerType: BenchmarkTriggerType;
  triggeredByUserId: string | null;
  gitCommit: string | null;
  gitBranch: string | null;
  knobsSnapshot: Record<string, unknown> | null;
  featureFlagsSnapshot: Record<string, unknown> | null;
  environmentSnapshot: EnvironmentSnapshot | null;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
};

export function useWorkflowSuitesQuery(params?: { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: ['benchmark-workflow-suites', params],
    queryFn: async (): Promise<{
      data: BenchmarkRun[];
      pagination: {
        page: number;
        pageSize: number;
        totalItems: number;
        totalPages: number;
      };
    }> => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));

      const res = await fetch(
        `${API_URL}/superadmin/benchmark/workflow-suites?${searchParams.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch workflow suites');
      }
      return res.json();
    },
    refetchInterval: 15_000,
  });
}

export function useWorkflowSuiteDetailQuery(id: string | null) {
  return useQuery({
    queryKey: ['benchmark-workflow-suite', id],
    queryFn: async (): Promise<{
      run: BenchmarkRun;
      workflowRuns: BenchmarkWorkflowRunMetrics[];
    }> => {
      if (!id) throw new Error('Suite ID required');
      const res = await fetch(`${API_URL}/superadmin/benchmark/workflow-suites/${id}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        if (res.status === 404) throw new Error('Suite not found');
        throw new Error('Failed to fetch workflow suite detail');
      }
      return res.json();
    },
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const run = query.state.data?.run;
      return run?.status === 'running' || run?.status === 'pending' ? 5000 : false;
    },
  });
}

type TriggerWorkflowSuiteInput = {
  configId: string;
  providerOverride?: string;
  modelOverride?: string;
};

export function useTriggerWorkflowSuiteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: TriggerWorkflowSuiteInput) => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/workflow-suites`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error ?? 'Failed to trigger workflow suite');
      }
      return data as { jobId: string; configId: string; configName: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-workflow-suites'] }).catch(() => {});
    },
  });
}

// ─── Ad-hoc A/B benchmark ────────────────────────────────────────────────

/** Promoted benchmark-org workflows available for an ad-hoc A/B run. */
export function useBenchmarkAbWorkflowsQuery(enabled = true) {
  return useQuery({
    queryKey: ['benchmark-ab-workflows'],
    queryFn: async (): Promise<{ workflows: Array<{ id: string; name: string }> }> => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/ab-workflows`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to fetch benchmark workflows');
      }
      return res.json();
    },
    enabled,
  });
}

type TriggerAdHocInput = {
  workflowIds: string[];
  runsPerArm: number;
  arms: BenchmarkArm[];
  providerOverride?: string;
  modelOverride?: string;
};

export function useTriggerAdHocBenchmarkMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: TriggerAdHocInput) => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/ad-hoc`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to trigger ad-hoc benchmark');
      }
      return (await res.json()) as { jobId: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-workflow-suites'] }).catch(() => {});
    },
  });
}

/** Ad-hoc A/B arm comparison report (run + per-run rows tagged with arm). */
export function useArmReportQuery(id: string | null) {
  return useQuery({
    queryKey: ['benchmark-arm-report', id],
    queryFn: async (): Promise<{ run: BenchmarkRun; rows: ArmComparisonRow[] }> => {
      if (!id) throw new Error('Suite ID required');
      const res = await fetch(
        `${API_URL}/superadmin/benchmark/workflow-suites/${id}/arm-report`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw await toApiError(res, 'Failed to fetch arm report');
      }
      return res.json();
    },
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const run = query.state.data?.run;
      return run?.status === 'running' || run?.status === 'pending' ? 5000 : false;
    },
  });
}

export function useWorkflowSuiteTrendsQuery(params?: {
  days?: number;
  workflowName?: string;
  configId?: string;
}) {
  return useQuery({
    queryKey: ['benchmark-workflow-trends', params],
    queryFn: async (): Promise<{ trends: BenchmarkWorkflowTrendPoint[] }> => {
      const searchParams = new URLSearchParams();
      if (params?.days) searchParams.set('days', String(params.days));
      if (params?.workflowName) searchParams.set('workflowName', params.workflowName);
      if (params?.configId) searchParams.set('configId', params.configId);

      const res = await fetch(
        `${API_URL}/superadmin/benchmark/workflow-suites/trends?${searchParams.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw await toApiError(res, 'Failed to fetch workflow suite trends');
      }
      return res.json();
    },
  });
}

export function useWorkflowRunDetailQuery(id: string | null) {
  return useQuery({
    queryKey: ['benchmark-workflow-run', id],
    queryFn: async (): Promise<{ workflowRun: BenchmarkWorkflowRunMetrics }> => {
      if (!id) throw new Error('Workflow run ID required');
      const res = await fetch(`${API_URL}/superadmin/benchmark/workflow-runs/${id}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        if (res.status === 404) throw new Error('Workflow run not found');
        throw new Error('Failed to fetch workflow run detail');
      }
      return res.json();
    },
    enabled: Boolean(id),
  });
}

export function useDeleteWorkflowSuiteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (suiteId: string) => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/workflow-suites/${suiteId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error ?? 'Failed to delete workflow suite run');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-workflow-suites'] }).catch(() => {});
    },
  });
}
